/**
 * T569 (FR-013-MIS) — 3-Way Mismatch banner.
 *
 * Renders a non-dismissable red banner when girocodeAmountMismatch is
 * true on the expense. The blocker stays until the user picks a
 * source via the resolve-mismatch click-target (T552 backend endpoint
 * /api/v1/expenses/:id/ocr-debug/resolve-mismatch).
 *
 * Phase-15 minimum: banner-only. The side-by-side three-source table
 * is a Wave-3 follow-up that lands when the 3-way data payload is
 * actually populated by the Layer-2 pipeline (eRechnung + Tesseract +
 * GiroCode all firing on the same beleg).
 */
import { useState } from 'react';
import { api } from '../../services/api';
import { useToastHelpers } from '../ui/Toast';

interface Props {
  expenseId: string;
  /** Pulled from ExpenseDetailDto.girocodeAmountMismatch. */
  mismatch: boolean | undefined;
  /** Refresh handler — callers pass their useQuery refetch. */
  onResolved?: () => void;
}

const FIELD_OPTIONS = [
  { value: 'gross_total', label: 'Brutto-Betrag' },
  { value: 'invoice_date', label: 'Rechnungsdatum' },
  { value: 'invoice_number', label: 'Rechnungsnummer' },
] as const;

const SOURCE_OPTIONS = [
  { value: 'erechnung', label: 'eRechnung (XML)' },
  { value: 'tesseract', label: 'Tesseract (OCR)' },
  { value: 'girocode', label: 'GiroCode (QR)' },
] as const;

export function GiroCodeMismatchBanner({ expenseId, mismatch, onResolved }: Props) {
  const [field, setField] = useState<(typeof FIELD_OPTIONS)[number]['value']>('gross_total');
  const [source, setSource] = useState<(typeof SOURCE_OPTIONS)[number]['value']>('erechnung');
  const [value, setValue] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const toast = useToastHelpers();

  if (!mismatch) return null;

  async function resolve() {
    if (!value.trim()) {
      toast.error('Bitte einen Wert für die ausgewählte Quelle eintragen.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/expenses/${expenseId}/ocr-debug/resolve-mismatch`, {
        field,
        source_choice: source,
        chosen_value:
          field === 'gross_total' ? Number(value.replace(',', '.')) : value,
      });
      toast.success('Mismatch aufgelöst');
      onResolved?.();
    } catch (e) {
      toast.error(`Auflösung fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      data-testid="girocode-mismatch-banner"
      className="rounded-lg border border-red-500/40 bg-red-950/40 p-4 space-y-3"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="text-2xl">⚠</span>
        <div className="flex-1">
          <p className="text-red-200 font-medium">
            3-Way-Mismatch: eRechnung, OCR und GiroCode stimmen nicht überein.
          </p>
          <p className="text-red-200/80 text-sm mt-1">
            Bitte ein Feld + eine Quelle wählen und auf "Auflösen" klicken.
            Der Beleg bleibt blockiert, bis ein Wert manuell bestätigt
            wurde (Audit-Trail: <code>ocr_mismatch_resolved</code>).
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <select
          value={field}
          onChange={(e) => setField(e.target.value as (typeof FIELD_OPTIONS)[number]['value'])}
          data-testid="girocode-mismatch-field"
          className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
        >
          {FIELD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={source}
          onChange={(e) => setSource(e.target.value as (typeof SOURCE_OPTIONS)[number]['value'])}
          data-testid="girocode-mismatch-source"
          className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={field === 'gross_total' ? '0,00' : 'Wert'}
          data-testid="girocode-mismatch-value"
          className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-white"
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={resolve}
          disabled={busy}
          data-testid="girocode-mismatch-resolve"
          className="px-3 py-1 rounded bg-red-500/30 hover:bg-red-500/40 border border-red-500/60 text-red-100 text-sm disabled:opacity-30"
        >
          {busy ? 'Speichere…' : 'Auflösen'}
        </button>
      </div>
    </div>
  );
}
